import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { cardsData } from "../../data/data";
import { itemsData } from "../../data/itemsData";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "../../components/Footer/Footer";
import Navbar from "../Navbar/Navabar";
import "./CategoryPage.css";

function CategoryPage() {
  let { subcategory, id } = useParams();
  const navigate = useNavigate();
  const category = cardsData.find(
    (group) => group.id.toString() === subcategory
  );

  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const toggleSubcategory = (item) => {
    setSelectedItems(itemsData[item.id] || []);
    setSelectedSubcategoryId(item.id);
    navigate(`/category/${subcategory}/${item.id}`);
  };

  const navigateToSubcategory = (subItem) => {
    navigate(`/subcategory/${selectedSubcategoryId}/${subItem.id}`, {
      state: subItem,
    });
  };

  const toggleExpand = (itemId) => {
    setExpandedSubcategories((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  useEffect(() => {
    if (id && itemsData[id]) {
      setSelectedItems(itemsData[id]);
      setSelectedSubcategoryId(id);
    }

    setTimeout(() => {
      setIsPageLoaded(true);
    }, 300);
  }, [id]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4 },
    },
  };

  return (
    <>
      <Navbar />
      <section className="banner-section">
        <div className="banner-text">
          <motion.h1
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            POULTRY
          </motion.h1>
          <motion.div
            className="banner-line"
            initial={{ width: 0 }}
            animate={{ width: "120px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
          ></motion.div>
        </div>
        <div className="banner-container">
          <div className="banner-overlay"></div>
          <img
            src="/images/poultrybanner4.avif"
            alt="Poultry banner"
            className="banner-image"
          />
        </div>
      </section>

      <motion.section
        className="category-content"
        initial="hidden"
        animate={isPageLoaded ? "visible" : "hidden"}
        variants={containerVariants}
      >
        <div className="container py-5">
          <motion.div className="category-header mb-4" variants={itemVariants}>
            <h2 className="fw-bold text-primary">{category?.title}</h2>
            <p className="lead text-muted">{category?.description}</p>
          </motion.div>

          <div className="row">
            <div className="col-lg-3 col-md-4">
              <motion.div className="subcategory-sidebar" variants={itemVariants}>
                <ul className="subcategory-list">
                  {category?.subitems.map((item) => (
                    <li key={item.id} className="subcategory-list-item">
                      <motion.div
                        className={`subcategory-item ${
                          +selectedSubcategoryId === item.id ? "active" : ""
                        }`}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleExpand(item.id)}
                      >
                        <span
                          className="subcategory-title"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSubcategory(item);
                          }}
                        >
                          {item.title}
                        </span>

                        <motion.span
                          animate={{
                            rotate: expandedSubcategories[item.id] ? 180 : 0,
                          }}
                          transition={{ duration: 0.3 }}
                          className="subcategory-icon"
                        >
                          {expandedSubcategories[item.id] ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </motion.span>
                      </motion.div>

                      <AnimatePresence>
                        {expandedSubcategories[item.id] && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              duration: 0.3,
                              ease: "easeInOut",
                            }}
                            className="subcategory-dropdown"
                          >
                            {item.items.length > 0 ? (
                              item.items.map((subItem, index) => (
                                <motion.li
                                  key={index}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{
                                    delay: index * 0.05,
                                    duration: 0.2,
                                  }}
                                  className="subcategory-dropdown-item"
                                  onClick={() =>
                                    navigateToSubcategory(subItem, subItem.id)
                                  }
                                  whileHover={{ x: 5 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  {subItem.name || subItem}
                                </motion.li>
                              ))
                            ) : (
                              <li className="text-muted px-2">
                                No items available
                              </li>
                            )}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            <div className="col-lg-9 col-md-8">
              <motion.div className="content-section" variants={itemVariants}>
                <h4 className="selected-category-title">
                  {selectedSubcategoryId
                    ? category?.subitems.find(
                        (item) => item.id === selectedSubcategoryId
                      )?.title
                    : "Selected Subcategory Items"}
                </h4>

                {selectedItems.length > 0 ? (
                  <motion.div
                    className="row g-4"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {selectedItems.map((subItem, index) => (
                      <motion.div
                        key={index}
                        className="col-lg-4 col-md-6 col-sm-6"
                        variants={itemVariants}
                        whileHover={{ y: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <div
                          className="product-card"
                          onClick={() => navigateToSubcategory(subItem, index)}
                        >
                          <div className="product-img-wrapper">
                            <img
                              src={subItem.img}
                              className="product-img"
                              alt={subItem.name}
                            />
                          </div>

                          <div className="product-body">
                            <h6 className="product-title">{subItem.name}</h6>
                          </div>

                          <div className="product-overlay">
                            <div className="product-overlay-content">
                              <h6 className="mb-2">{subItem.name}</h6>
                              <p className="product-description">
                                {subItem.description}
                              </p>
                              <span className="view-details">View Details</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    className="empty-state"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <p className="text-muted">
                      Select a subcategory to view details.
                    </p>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      <Footer />
    </>
  );
}

export default CategoryPage;